import React from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from 'components/ScrollToTop';
import ErrorBoundary from 'components/ErrorBoundary';
import { useAuth } from './contexts/AuthContext';
import NotFound from 'pages/NotFound';
import LoginPage from './pages/login';
import Dashboard from './pages/dashboard';
import ProgressReports from './pages/progress-reports';
import ActivityTracking from './pages/activity-tracking';
import NutritionTracking from './pages/nutrition-tracking';
import ProfileSettings from './pages/profile-settings';
import Register from './pages/register';
import SleepAnalysis from './pages/sleep-analysis';
import WorkoutCatalog from './pages/workout-catalog';
import WorkoutManagement from './pages/workout-management';
import Communication from './pages/communication';
import CommunicationHub from './pages/communication-hub';
import ProgressTracking from './pages/progress-tracking';
import WorkoutBuilder from './pages/workout-builder';
import TrainerDashboard from './pages/trainer-dashboard';
import ClientAssessment from './pages/client-assessment';
import PredictiveAnalyticsDashboard from './pages/predictive-analytics-dashboard';
import DeviceIntegrationHub from './pages/device-integration-hub';
import RealTimeDataSync from './pages/real-time-data-sync';
import ClientWearableMetricsDashboard from './pages/client-wearable-metrics-dashboard';
import TrainerWorkoutBuilder from './pages/trainer-workout-builder';
import ClientWorkoutDashboard from './pages/client-workout-dashboard';
import TrainerNutritionPlanBuilder from './pages/trainer-nutrition-plan-builder';
import ClientPlansPage from './pages/client-plans';
import PricingPage from './pages/pricing';
import CheckoutPage from './pages/checkout';
import PostPaymentRegisterPage from './pages/post-payment-register';
import RegistrationConfirmationPage from './pages/registration-confirmation';
import LandingPage from './pages/landing';
import OnboardingPage from './pages/onboarding';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/post-payment-register" element={<PostPaymentRegisterPage />} />
          <Route path="/registration-confirmation" element={<RegistrationConfirmationPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route
            path="/"
            element={<LandingPage />}
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer-dashboard"
            element={
              <ProtectedRoute>
                <TrainerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client-assessment"
            element={
              <ProtectedRoute>
                <ClientAssessment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/predictive-analytics-dashboard"
            element={
              <ProtectedRoute>
                <PredictiveAnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/device-integration-hub"
            element={
              <ProtectedRoute>
                <DeviceIntegrationHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/real-time-data-sync"
            element={
              <ProtectedRoute>
                <RealTimeDataSync />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client-wearable-metrics-dashboard"
            element={
              <ProtectedRoute>
                <ClientWearableMetricsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer-workout-builder"
            element={
              <ProtectedRoute>
                <TrainerWorkoutBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client-workout-dashboard"
            element={
              <ProtectedRoute>
                <ClientWorkoutDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer-nutrition-plan-builder"
            element={
              <ProtectedRoute>
                <TrainerNutritionPlanBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client-plans"
            element={
              <ProtectedRoute>
                <ClientPlansPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workout-catalog"
            element={
              <ProtectedRoute>
                <WorkoutCatalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workout-builder"
            element={
              <ProtectedRoute>
                <WorkoutBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workout-management"
            element={
              <ProtectedRoute>
                <WorkoutManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/communication"
            element={
              <ProtectedRoute>
                <Communication />
              </ProtectedRoute>
            }
          />
          <Route path="/communication-hub" element={<CommunicationHub />} />
          <Route
            path="/progress-tracking"
            element={
              <ProtectedRoute>
                <ProgressTracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/progress-reports"
            element={
              <ProtectedRoute>
                <ProgressReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activity-tracking"
            element={
              <ProtectedRoute>
                <ActivityTracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/nutrition-tracking"
            element={
              <ProtectedRoute>
                <NutritionTracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile-settings"
            element={
              <ProtectedRoute>
                <ProfileSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sleep-analysis"
            element={
              <ProtectedRoute>
                <SleepAnalysis />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
